import { ArrowDownTrayIcon, DocumentTextIcon, DocumentIcon } from "@heroicons/react/24/outline";
import LoadingSpinner from "components/common/LoadingSpinner";
import toast from "react-hot-toast";
import { useLanguage } from "contexts/LanguageContext";
import { Template } from "types/template";
import { useEffect, useState } from "react";

interface GeneratePageProps {
  selectedTemplate: Template | null;
  selectedFormats: string[];
  setSelectedFormats: (formats: string[]) => void;
  isGenerating: boolean;
  handleGenerateAndClose: () => void;
  sortedDocumentData: string[][];
  filledPercentage: number;
  filledCount: number;
  generatedFileUrl?: string | null;
  generatedFileName?: string | null;
  generatedFiles?: {url: string, fileName: string}[];
}

const GeneratePage = ({
  selectedTemplate,
  selectedFormats,
  setSelectedFormats,
  isGenerating,
  handleGenerateAndClose,
  sortedDocumentData,
  filledPercentage,
  filledCount,
  generatedFileUrl,
  generatedFileName,
  generatedFiles = [],
}: GeneratePageProps) => {
  const { t, language } = useLanguage();
  const [isGenerated, setIsGenerated] = useState(false);

  // Reset generation state when component mounts or when generatedFileUrl changes
  useEffect(() => {
    setIsGenerated(!!generatedFileUrl);
  }, [generatedFileUrl]);

  // Biztosítjuk, hogy csak a DOCX legyen kiválasztva (PDF nem támogatott POD táblázat miatt)
  useEffect(() => {
    // Remove PDF from selected formats - not supported with POD tables
    if (selectedFormats.includes('pdf')) {
      setSelectedFormats(selectedFormats.filter(format => format !== 'pdf'));
    }
    
    // Ensure DOCX is always selected
    if (!selectedFormats.includes('docx')) {
      setSelectedFormats(['docx']);
    }
  }, [selectedFormats, setSelectedFormats]);

  const handleGenerate = () => {
    try {
      handleGenerateAndClose();
      setIsGenerated(true);
      toast.success(t("documents.generated"));
    } catch (error) {
      toast.error(t("documents.generationError"));
    }
  };

  // PDF formátum hozzáadása vagy eltávolítása - LETILTVA
  const togglePdfFormat = () => {
    // PDF nem támogatott a POD táblázat miatt
    toast.error(language === 'hu' 
      ? 'PDF generálás nem támogatott. Kérjük, töltse le a DOCX-et és konvertálja PDF-re Word-ben (Fájl → Mentés másként → PDF).'
      : 'PDF generation is not supported. Please download DOCX and convert to PDF in Word (File → Save As → PDF).');
  };

  // Sikeres generálás utáni nézet
  if (isGenerated && generatedFileUrl) {
    // Keressük meg a DOCX fájlt a generált fájlok között
    const docxFile = generatedFiles.find(file => file.fileName.toLowerCase().endsWith('.docx'));
    
    return (
      <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-16 h-16 mb-6 flex items-center justify-center rounded-full bg-gradient-to-br from-green-100 to-teal-100">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <h3 className="text-xl font-medium text-gray-800 mb-2">
            {t('documents.generationSuccess')}
          </h3>
          
          <p className="text-gray-600 text-center mb-6 max-w-md">
            {t('documents.generationSuccessDescription')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
            {/* Only DOCX download is shown */}
            {docxFile && (
              <a 
                href={docxFile.url}
                download={docxFile.fileName}
                className="flex items-center justify-center px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
              >
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                {t('documents.downloadDocument')} (DOCX)
              </a>
            )}
            
            {/* Ha nincs meghatározva külön formátum, akkor az eredeti letöltési link */}
            {!docxFile && (
              <a 
                href={generatedFileUrl}
                download={generatedFileName || "document.docx"}
                className="flex items-center justify-center px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
              >
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                {t('documents.downloadDocument')} (DOCX)
              </a>
            )}
          </div>

          <div className="mt-8 border-t border-gray-200 pt-6 w-full">
            <div className="flex flex-col space-y-2 items-left">
              {/* Minden generált fájl listája (DOCX és PDF) */}
              {generatedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-teal-500 mr-2" />
                    <span className="text-sm text-gray-700">{file.fileName}</span>
                  </div>
                  <a
                    href={file.url}
                    download={file.fileName}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    {t('documents.preview.download')}
                  </a>
                </div>
              ))}
              
              {/* Ha nincs generált fájl, akkor az eredeti fájlnév */}
              {generatedFiles.length === 0 && generatedFileName && (
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-gray-500 mr-2" />
                  <span className="text-sm text-gray-700">{generatedFileName}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
      <h3 className="text-sm font-medium text-gray-800 mb-3">
        {t('documents.generateDocument')}
      </h3>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Sablon információ */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 flex-1">
            <h4 className="text-xs font-medium text-gray-600 mb-2">{t('documents.selectedTemplate')}</h4>
            <div className="flex items-center">
              <DocumentTextIcon className="h-5 w-5 text-teal-600 mr-2" />
              <div>
                <div className="text-sm font-medium text-gray-800">{selectedTemplate?.name}</div>
                <div className="text-xs text-gray-500">{selectedTemplate?.description}</div>
              </div>
            </div>
          </div>

          {/* Ügyfél információ */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 flex-1">
            <h4 className="text-xs font-medium text-gray-600 mb-2">{t('documents.customerData')}</h4>
            <div className="text-sm text-gray-800">
              <div className="text-xs text-gray-500 mt-1 flex items-center justify-between">
                <span>{t('documents.fieldsInTemplate', { count: sortedDocumentData.length })}</span>
                <span className={`${filledPercentage > 70 ? 'text-green-600' : filledPercentage > 30 ? 'text-amber-600' : 'text-red-600'}`}>
                  {filledCount} {t('documents.filled')} ({filledPercentage}%)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Formátum választó */}
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 mt-4">
          <h4 className="text-xs font-medium text-gray-600 mb-2">{t('documents.chooseOutputFormat')}</h4>
          <div className="flex items-center justify-center space-x-3 max-w-xs mx-auto">
            <button
              type="button"
              onClick={() => {
                setSelectedFormats(['docx']);
                toast.success(t("documents.formatSetToDocx"));
              }}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors duration-200 ${selectedFormats.includes('docx')
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              DOCX
            </button>
          </div>
          <div className="mt-3 text-xs text-gray-500 text-center">
            {language === 'hu' 
              ? 'PDF-hez: Töltse le a DOCX-et és mentse PDF-ként Word-ben'
              : 'For PDF: Download DOCX and save as PDF in Word'}
          </div>
        </div>

        {/* Generálás gomb */}
        <div className="flex justify-center mt-2">
          <button
            onClick={handleGenerate}
            disabled={isGenerating || selectedFormats.length === 0}
            className={`px-6 py-3 font-medium text-white rounded-md ${isGenerating || selectedFormats.length === 0
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-teal-600 hover:bg-teal-700'
              }`}
          >
            {isGenerating ? (
              <div className="flex items-center justify-center">
                <LoadingSpinner size="sm" className="mr-2" />
                <span>{selectedFormats.includes('pdf') ? 
                  t('documents.generatingDocumentWithPdf') : 
                  t('documents.generatingDocument')}</span>
              </div>
            ) : (
              t('documents.generateDocument')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GeneratePage;

